import { useMemo } from 'react'

import { useGetTrackedGamesQuery } from '../../../api/core'
import { useLiveEventsTrackerAccessCheck } from '../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { useTrackedEvent } from '../../live-events/hooks/useTrackedEvent'
import { TrackedGame } from '../../live-events/types/TrackedGame'
import { UnifiedNewsEntry } from '../types/UnifiedNewsEntry'

type NewsEntryLiveEventBlueprint = {
  trackedGame?: TrackedGame
  gameLevelBlueprintId?: string
}

/**
 * Resolves what `useBlueprintFamilyForEvent` needs for a live-event news
 * entry: the owning tracked game and the event's game-level blueprint id. The
 * feed entry itself carries neither (only `gameId`, `typeId` and `eventId`),
 * so this fetches the tracked event and looks the game up in the tracked-games
 * list, which the rest of the tracker shares through the RTK cache.
 *
 * Both requests are skipped without `RoleEnum.live_events_tracker`: the family crumb is
 * the only thing they feed, and without the entitlement it would neither be
 * named nor linked. Category and event type need only the entry's `typeId`.
 */
export const useNewsEntryLiveEventBlueprint = (newsEntry: UnifiedNewsEntry, enabled = true): NewsEntryLiveEventBlueprint => {
  const userLanguage = useCurrentUserLanguage()
  const hasLiveEventsTrackerAccess = useLiveEventsTrackerAccessCheck()
  const eventId = newsEntry.feedEntry.entryData.eventId
  const trackedGameId = newsEntry.feedEntry.gameId
  const skip = !enabled || !hasLiveEventsTrackerAccess || !eventId

  const { data: trackedEvent } = useTrackedEvent(eventId, { skip })
  const { data: trackedGames } = useGetTrackedGamesQuery({ userLanguage }, { skip })

  const trackedGame = useMemo(() => trackedGames?.find((game) => game.game.id === trackedGameId), [trackedGames, trackedGameId])

  return { trackedGame, gameLevelBlueprintId: trackedEvent?.event.blueprintEvolutionData?.blueprintId }
}
